import { ChevronRightIcon } from "@heroicons/react/outline";
import { ExternalLinkIcon } from "@heroicons/react/solid";
import { FlagPicker } from "apollo/components/chat/mobile-chat/flag-picker";
import { useDetailedUser } from "apollo/components/members/hooks/useDetailedUsers";
import { getMembershipStatus } from "apollo/components/members/utils/transformUsersForMemberTable";
import type { membershipStatus as MembershipStatusType } from "apollo/components/members/types/types";
import { useCompactShell } from "apollo/components/mobile/use-compact-shell";
import Link from "next/link";
import { useRoomAndUser } from "queries/chat/rooms/hooks";
import { useCurrentUser } from "queries/current-user";
import React, { useEffect, useMemo } from "react";
import { toast } from "react-hot-toast";
import ReactTooltip from "react-tooltip";
import useClipboard from "react-use-clipboard";
import { useChat } from "ui/components/chat/container";
import { useChatSidePanel } from "ui/components/chat/container/side-panel";
import { FlaggingSystem } from "ui/components/chat/flagging";
import { FlaggingSystemProvider } from "ui/components/chat/flagging/container";
import { buildShortName } from "ui/components/chat/name-helper";
import Clipboard from "ui/components/icons/Clipboard";
import InfoFilled from "ui/components/icons/InfoFilled";
import Message from "ui/components/icons/Message";
import UserFilled from "ui/components/icons/UserFilled";
import { Suspended } from "ui/components/suspended";
import { useIsMobile } from "ui/lib/use-breakpoint";
import { cn } from "ui/lib/utils";

// GraphQL fields needed for the user info panel
// Note: height/weight are not available in the GraphQL schema
const userInfoPanelFields = {
  id: true,
  givenName: true,
  familyName: true,
  email: true,
  profileImgImgixURL: true,
  gender: true,
  dob: true,
  roleName: true,
  membershipStatus: true,
  trialExpirationTS: true,
  lastActivityTS: true,
  lastCompletedWorkoutCompletedTS: true,
  lastCompletedWorkoutRating: true,
  avgWorkoutRating: true,
  numWorkoutsCompleted: true,
  createdTS: true,
  subscriptionCurrentPeriodEndTS: true,
  subscriptionType: true,
  tags: true,
  isPushEnabled: true,
  note: true,
  coachRoomID: true,
  teamID: true,
};

// Type for the GraphQL response
// Note: height/weight are not available in GraphQL schema
interface GraphQLDetailedUser {
  id: string;
  givenName: string;
  familyName: string;
  email: string;
  profileImgImgixURL: string | null;
  gender: string | null;
  dob: string | null;
  roleName: string | null;
  membershipStatus: string | null;
  trialExpirationTS: string | null;
  lastActivityTS: string | null;
  lastCompletedWorkoutCompletedTS: string | null;
  lastCompletedWorkoutRating: number | null;
  avgWorkoutRating: number | null;
  numWorkoutsCompleted: number | null;
  createdTS: string | null;
  subscriptionCurrentPeriodEndTS: string | null;
  subscriptionType: string | null;
  tags: string | null;
  isPushEnabled: boolean | null;
  note: string | null;
  coachRoomID: string | null;
  teamID: string | null;
}

// Transformed user type matching existing component expectations
interface TransformedUser {
  id: string;
  givenName: string;
  familyName: string;
  email: string;
  profileImg: string | null;
  gender: string | null;
  dob: string | null;
  role: string | null;
  membershipStatus: string | null;
  trialExpiration: string | null;
  lastActivityAt: string | null;
  lastCompletedWorkout: {
    completedAt: string;
    rating: number | null;
  } | null;
  avgWorkoutRating: number | null;
  numWorkoutsCompleted: number | null;
  createdAt: string | null;
  subscription: {
    renewalDate: string;
    offeringName: string | null;
  } | null;
  tags: string | null;
  isPushEnabled: boolean | null;
  notes: string | null;
  coachRoomId: string | null;
  teamId: string | null;
}

// Transform GraphQL response to match existing component field names
function transformGraphQLUser(
  graphqlUser: GraphQLDetailedUser | null
): TransformedUser | null {
  if (!graphqlUser) return null;
  return {
    id: graphqlUser.id,
    givenName: graphqlUser.givenName,
    familyName: graphqlUser.familyName,
    email: graphqlUser.email,
    profileImg: graphqlUser.profileImgImgixURL,
    gender: graphqlUser.gender,
    dob: graphqlUser.dob,
    role: graphqlUser.roleName,
    membershipStatus: graphqlUser.membershipStatus,
    trialExpiration: graphqlUser.trialExpirationTS,
    lastActivityAt: graphqlUser.lastActivityTS,
    lastCompletedWorkout: graphqlUser.lastCompletedWorkoutCompletedTS
      ? {
          completedAt: graphqlUser.lastCompletedWorkoutCompletedTS,
          rating: graphqlUser.lastCompletedWorkoutRating,
        }
      : null,
    avgWorkoutRating: graphqlUser.avgWorkoutRating,
    numWorkoutsCompleted: graphqlUser.numWorkoutsCompleted,
    createdAt: graphqlUser.createdTS,
    subscription: graphqlUser.subscriptionCurrentPeriodEndTS
      ? {
          renewalDate: graphqlUser.subscriptionCurrentPeriodEndTS,
          offeringName: graphqlUser.subscriptionType,
        }
      : null,
    tags: graphqlUser.tags,
    isPushEnabled: graphqlUser.isPushEnabled,
    notes: graphqlUser.note,
    coachRoomId: graphqlUser.coachRoomID,
    teamId: graphqlUser.teamID,
  };
}
import { ageOn, birthdayLabel } from "./birthday";
import {
  AverageWorkoutRating,
  Header,
  LastActive,
  LastWorkout,
  LastWorkoutRating,
  MemberHealth,
  NotesComponent,
  RenewalDate,
  SubscriptionType,
  Tags,
  TrialExpiration,
  UserCreated,
  UserRoleAndStatus,
} from "./components";

// Accounts without a real address carry a hash in its place.
const isHashedEmail = (email: string) => /^[a-f0-9]{32,}(@|$)/i.test(email);

const Container = ({ children }) => {
  const shouldShowInModal = useIsMobile();
  return (
    <div
      style={{ minWidth: shouldShowInModal ? "100%" : 350 }}
      className="relative z-10 overflow-x-hidden overflow-y-auto md:w-1/5 bg-background"
    >
      {children}
    </div>
  );
};

type HeaderProps = {
  /** For a caller that puts the panel under its own header with back. */
  hideHeader?: boolean;
};

const FallBack = ({ hideHeader }: HeaderProps) => {
  return (
    <Container>
      {hideHeader ? null : <Header />}
      <div className="flex-col p-6 text-foreground flex-center">
        <div
          className="mb-8 rounded-full shadow-ms bg-secondary skeleton-box"
          style={{ width: 120, height: 120 }}
        />

        <div className="flex-col w-full mb-6 flex-center">
          <div
            className="w-1/2 mb-2 space-x-4 rounded-full skeleton-box flex-center align-center"
            style={{ height: 20 }}
          />
          <div
            className="w-2/3 mb-2 space-x-4 rounded-full skeleton-box flex-center align-center"
            style={{ height: 10 }}
          />
        </div>

        <div className="flex flex-col w-full mb-16 space-y-4">
          {[...Array(2)].map((_, ndx) => {
            return (
              <div
                key={ndx}
                className="w-full mb-2 space-x-4 rounded skeleton-box flex-center align-center"
                style={{ height: 30 }}
              />
            );
          })}
        </div>
        <div className="flex flex-col w-full space-y-4">
          {[...Array(12)].map((_, ndx) => {
            return (
              <div
                key={ndx}
                className="w-full mb-2 space-x-4 rounded skeleton-box flex-center align-center"
                style={{ height: 10 }}
              />
            );
          })}
        </div>
      </div>
    </Container>
  );
};

const CopyValue = ({ value }) => {
  const [isCopied, setCopied] = useClipboard(value, {
    successDuration: 2500,
  });

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [isCopied]);

  return (
    <div>
      <ReactTooltip
        id={`copy-user-id-${value}`}
        backgroundColor="#E7EFFF"
        textColor="#0F0F12"
      />
      <button
        type="button"
        aria-label={isCopied ? "Member ID copied" : "Copy member ID"}
        onClick={setCopied}
        key={value + isCopied}
        data-for={`copy-user-id-${value}`}
        data-tip={isCopied ? "Copied!" : "Copy"}
        data-place={"left"}
        data-effect="solid"
        className="justify-start w-full max-w-full px-3 py-1 overflow-hidden text-left rounded-full bg-charcoal flex-center"
      >
        <span className="w-16 text-xs uppercase truncate">{value}</span>
        <Clipboard className="w-4 text-foreground" />
      </button>
    </div>
  );
};

type ChatRoomContext = {
  roomDictionary: Record<string, any>;
  activeRoomID?: string | null;
  updateActiveRoom: (room: any) => void;
};

type MemberInfoProps = HeaderProps & {
  userId: string;
  /** In chat: Message switches the room on screen and flags apply to the member's room. */
  chat?: ChatRoomContext;
};

const MemberInfo = ({ userId, hideHeader, chat }: MemberInfoProps) => {
  const { currentUser } = useCurrentUser();
  const compact = useCompactShell();

  // Use GraphQL endpoint instead of REST /users/detailed
  // Include "userInfoPanel" in query key to prevent cache collision with FlaggingSystem
  const { data: graphqlData, isLoading } = useDetailedUser(
    {
      node: userInfoPanelFields,
      filter: userId ? { id: { eq: userId } } : undefined,
      active: !!userId,
    } as any,
    ["userInfoPanel", userId]
  );

  // Transform GraphQL response to match existing field names
  const user = useMemo(() => {
    const graphqlUser = (
      graphqlData as {
        detailedUsers?: { edges?: { node: GraphQLDetailedUser }[] };
      }
    )?.detailedUsers?.edges?.[0]?.node;
    return transformGraphQLUser(graphqlUser ?? null);
  }, [graphqlData]);

  const { data: roomResponse } = useRoomAndUser(
    chat ? (user?.coachRoomId as string) : "",
    chat?.roomDictionary[chat?.activeRoomID as string]
  );
  const room = useMemo(() => roomResponse?.room, [roomResponse]);

  const { givenName = "", familyName = "" } = user || {};

  const membershipStatus = useMemo(
    () =>
      user &&
      getMembershipStatus({
        membershipStatus: user.membershipStatus as MembershipStatusType,
        trialExpirationTS: user.trialExpiration as unknown as Date,
      }),
    [user]
  );

  useEffect(() => {
    if (!chat) return;
    if (isLoading) return;
    if (!!user) return;
    toast.error("Sorry! I can't find that member", {
      position: "top-center",
      style: {
        borderRadius: "500px",
        background: "#333",
        color: "#fff",
      },
    });
  }, [user, isLoading, !!chat]);

  const shortName = buildShortName({ givenName, familyName });
  const messageLabel = `Message ${shortName.trim() || "member"}`;
  // In chat, the member's room once it's loaded and isn't already on screen;
  // elsewhere, the coach room in their team's chat.
  const coachRoomHref =
    !chat && user?.coachRoomId && user?.teamId
      ? `/programs/${user.teamId}/chat?roomId=${user.coachRoomId}`
      : null;
  const canMessage = chat
    ? !!room &&
      !!user?.coachRoomId &&
      !!chat.activeRoomID &&
      !chat.activeRoomID?.includes?.(user?.coachRoomId)
    : !!coachRoomHref;
  const openCoachRoom = () => chat?.updateActiveRoom(room);
  const age = ageOn(user?.dob);

  if (isLoading) return <FallBack hideHeader={hideHeader} />;
  if (!user) {
    return chat ? null : (
      <p className="px-4 py-16 text-center text-[15px] text-white/60">
        Couldn&apos;t find this member.
      </p>
    );
  }

  const messagePillClass =
    "mt-4 inline-flex h-11 max-w-full items-center gap-2 rounded-full bg-primary px-5 text-[15px] font-semibold text-primary-foreground transition-opacity active:opacity-80";
  const messagePillBody = (
    <>
      <Message aria-hidden="true" className="size-5 shrink-0" />
      <span className="truncate">{messageLabel}</span>
    </>
  );

  return (
    <Container>
      <div className={cn({ hidden: !user })}>
        {hideHeader ? null : <Header />}
        <div
          style={{ height: (!compact && "calc(100vh - 69px)") || "" }}
          className="relative pb-4 overflow-x-hidden md:overflow-y-scroll md:top-2"
        >
          <div className="space-y-6">
            <div className="flex-col px-4 pt-4 flex-center">
              <div className="flex-center">
                <div className="relative flex-center">
                  {user?.profileImg ? (
                    <div
                      className="bg-center bg-no-repeat bg-cover rounded-full shadow-md"
                      style={{
                        backgroundImage: `url(${user?.profileImg}?w=200)`,
                        width: 120,
                        height: 120,
                      }}
                    />
                  ) : (
                    <div
                      className="bg-secondary rounded-full shadow-md center"
                      style={{
                        width: 120,
                        height: 120,
                      }}
                    >
                      <UserFilled className="w-12 text-muted-foreground" />
                    </div>
                  )}
                  {user?.numWorkoutsCompleted ? (
                    <div
                      className="absolute -translate-x-10 bg-center bg-no-repeat bg-cover rounded-full"
                      style={{
                        backgroundImage: `url(https://ladderteams.imgix.net/badges/badge-${user?.numWorkoutsCompleted}.png)`,
                        transform: "translate(50px, -30px)",
                        width: 60,
                        height: 60,
                      }}
                    ></div>
                  ) : null}
                </div>
              </div>
              <div className="max-w-full px-4 pt-4 text-center">
                <h3 className="text-2xl font-bold text-foreground truncate">
                  {shortName}
                </h3>
                {currentUser?.role === "admin" &&
                user?.email &&
                !isHashedEmail(user.email) ? (
                  <h3 className="text-sm text-foreground/75 truncate">
                    {user.email}
                  </h3>
                ) : null}
              </div>
              {compact && canMessage ? (
                coachRoomHref ? (
                  <Link href={coachRoomHref} className={messagePillClass}>
                    {messagePillBody}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={openCoachRoom}
                    className={messagePillClass}
                  >
                    {messagePillBody}
                  </button>
                )
              ) : null}
              {/* Note: height/weight not available in GraphQL schema */}
              <div className="justify-center w-full mt-6 space-x-6 flex-center">
                <div className="flex-col text-foreground flex-center">
                  <span className="text-xs opacity-50">Gender</span>
                  <span>{user?.gender || "-"}</span>
                </div>
                {compact ? (
                  // No hover on touch, so the birthday sits beside the age.
                  <div className="flex-col text-foreground flex-center">
                    <span className="text-xs opacity-50">Age</span>
                    <span>
                      {age === null
                        ? "-"
                        : `${age} · ${birthdayLabel(user.dob)}`}
                    </span>
                  </div>
                ) : (
                  <>
                    <ReactTooltip id="user-age" />
                    <div
                      data-for={`user-age`}
                      data-tip={birthdayLabel(user?.dob, true)}
                      data-effect="solid"
                      className="relative flex-col text-foreground flex-center"
                    >
                      <span className="text-xs opacity-50">Age</span>
                      <span>{age === null ? "-" : age}</span>
                      <InfoFilled className="absolute top-0 right-0 w-3 -mr-4 text-foreground" />
                    </div>
                  </>
                )}
              </div>
            </div>
            {chat ? (
              <div className="z-50 flex-col w-full px-3 flex-center">
                <FlaggingSystemProvider>
                  <FlaggingSystem menu={compact ? <FlagPicker /> : undefined} />
                </FlaggingSystemProvider>
              </div>
            ) : null}
            <div className="px-4 text-foreground">
              {!!membershipStatus ? (
                <UserRoleAndStatus
                  role={user?.role}
                  status={membershipStatus}
                />
              ) : null}
              <MemberHealth
                lastActivityAt={user?.lastActivityAt}
                lastWOCompletedAt={user?.lastCompletedWorkout?.completedAt}
              />
              <LastActive value={user?.lastActivityAt} />
              {user?.lastCompletedWorkout ? (
                <LastWorkout value={user?.lastCompletedWorkout?.completedAt} />
              ) : null}
              {user?.lastCompletedWorkout ? (
                <LastWorkoutRating value={user?.lastCompletedWorkout?.rating} />
              ) : null}
              {user?.avgWorkoutRating ? (
                <AverageWorkoutRating value={user?.avgWorkoutRating} />
              ) : null}
              <UserCreated value={user?.createdAt} />
              {user?.subscription ? (
                <RenewalDate value={user?.subscription?.renewalDate} />
              ) : null}
              {user?.subscription ? (
                <SubscriptionType value={user?.subscription?.offeringName} />
              ) : null}
              <TrialExpiration value={user?.trialExpiration} />
              <Tags value={user?.tags} stacked={compact} />
              <div className="flex items-center justify-between py-2">
                <label className="text-[13px] text-muted-foreground">
                  Push notifications
                </label>
                <span>
                  {"boolean" === typeof user?.isPushEnabled
                    ? user?.isPushEnabled
                      ? "Yes"
                      : "No"
                    : "Unknown"}
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <label className="text-[13px] text-muted-foreground">
                  User ID
                </label>
                <CopyValue value={user?.id} />
              </div>
              {compact ? (
                // On a phone the profile is the next screen, not a new tab.
                <Link
                  href={`/members/${user?.id}`}
                  className="-mx-4 flex min-h-11 items-center justify-between px-4 py-2 active:bg-white/[0.05]"
                >
                  <span className="text-[15px] text-foreground">
                    Full profile
                  </span>
                  <ChevronRightIcon
                    aria-hidden="true"
                    className="size-4 text-faint"
                  />
                </Link>
              ) : (
                <div className="flex items-center justify-between py-2">
                  <label className="text-[13px] text-muted-foreground">
                    Entitlements
                  </label>
                  <Link
                    passHref
                    href={`/members/${user?.id}`}
                    target="_blank"
                    className={cn(
                      "rounded-full flex space-x-2 text-sm px-3 py-1 shadow-button bg-charcoal"
                    )}
                  >
                    <span>Open</span>
                    <ExternalLinkIcon className="w-4" />
                  </Link>
                </div>
              )}
              <NotesComponent
                key={userId}
                selectedUserId={userId}
                remoteNotes={user?.notes}
                autoGrow={compact}
              />
            </div>
          </div>
        </div>
        {!compact && canMessage ? (
          <div className="">
            <button
              onClick={openCoachRoom}
              aria-label={messageLabel}
              title={messageLabel}
              className="absolute top-0 right-0 justify-between p-3 mx-3 my-4 rounded-full shadow-lg md:top-auto md:bottom-0 bg-primary flex-center"
            >
              <Message aria-hidden="true" className="w-8 text-black" />
            </button>
          </div>
        ) : null}
      </div>
    </Container>
  );
};

export const UserInfoComponent = React.memo(({ hideHeader }: HeaderProps) => {
  const {
    infoPanelActive,
    selectedUserId,
    roomDictionary,
    activeRoomID,
    updateActiveRoom,
  } = useChat();
  if (!infoPanelActive) return null;
  return (
    <MemberInfo
      userId={selectedUserId}
      hideHeader={hideHeader}
      chat={{ roomDictionary, activeRoomID, updateActiveRoom }}
    />
  );
});

export const UserInfoPanel = ({ hideHeader }: HeaderProps) => {
  const { open: sidePanelOpen } = useChatSidePanel();
  if (sidePanelOpen) return null;
  return (
    <Suspended fallback={<FallBack hideHeader={hideHeader} />}>
      <UserInfoComponent hideHeader={hideHeader} />
    </Suspended>
  );
};

/** Member info outside chat (a search result on a phone), with Message opening their room. */
export const MemberInfoScreen = ({ userId }: { userId: string }) => (
  <Suspended fallback={<FallBack hideHeader />}>
    <MemberInfo userId={userId} hideHeader />
  </Suspended>
);
